<script lang="ts">
  import { t } from "@/libs/i18n"
  import { includes } from "@/libs/lodash"
  import { clamp } from "@/libs/math"
  import { CircleScanFrame, Reel, StatusBacklight, fade } from "@/libs/ui"
  import CameraFrame from "@/libs/ui/CameraFrame"
  import { AXIS, DIRECTION } from "@/libs/ui/CircleScanFrame"
  import { ICON, Icon } from "@/libs/ui/Icon"
  import IlluminationDirection from "@/libs/ui/IlluminationDirection"
  import { VARIANT } from "@/libs/ui/StatusBacklight/StatusBacklight.svelte"
  import { createFlow } from "@/libs/ui/flow"
  import { untrack, type ComponentProps } from "svelte"
  import { getFaceAngleDeviationMessageKey } from "../../../messages"
  import { getGuideDirection, getGuideRotation } from "../../../scan-frame-guide"
  import {
    LIGHT_AZIMUTH_THRESHOLDS,
    LIGHT_INTENSITY_THRESHOLDS,
    PITCH_ANGLE_THRESHOLDS,
    SIDE_CAPTURE_FACE_ANGLE,
    YAW_ANGLE_THRESHOLDS,
  } from "../../checks"
  import type { Detections } from "../../preset"
  import { ANGLE_DEVIATION_STAGES, STAGE, Stages } from "../../stages"
  import IlluminationIntensity from "./IlluminationIntensity.svelte"
  import Layout from "./Layout.svelte"
  import { ANGLE_DEVIATION_MESSAGE_MAP } from "./messages"

  let {
    detections,
    stage,
    facing = "front",
    onChange,
    ...props
  }: {
    detections: Detections | null
    stage: STAGE | null
    facing?: "front" | "back"
    onChange(stage: STAGE): void
  } & Omit<
    ComponentProps<typeof Layout>,
    "faceBox" | "frontLayout" | "indicator" | "guidance"
  > = $props()

  const flow = createFlow(Stages)

  let left = $state(0)
  let right = $state(0)

  $effect(() => {
    const nextStage = stage || STAGE.CAPTURING_DONE

    if (!detections || $flow.stage === nextStage) return

    flow.go(
      stage,
      !stage || stage === STAGE.CAPTURING_DONE
        ? undefined
        : {
            transitionDuration:
              ([STAGE.ROTATING_LEFT, STAGE.ROTATING_RIGHT].includes(stage) ? 1000 : null) ??
              ([STAGE.CAPTURING_RIGHT, STAGE.CAPTURING_RIGHT].includes(stage) ? 0 : null) ??
              undefined,
          },
    )
  })

  // this weird notation is a workaround for the issue: https://hautai.atlassian.net/browse/LIQA-1161?focusedCommentId=22678
  let confirmation = $derived({ valueOf: () => $flow.running })
  let message = $state<string>()

  $effect(() => {
    if (!$flow.stage) return

    if (includes(ANGLE_DEVIATION_STAGES, $flow.stage)) {
      const key = getFaceAngleDeviationMessageKey(
        ANGLE_DEVIATION_MESSAGE_MAP[$flow.stage],
        detections?.face?.rotation,
      )

      if (key) message = $t.face_180_live_video.face_angle_deviation[key]
    } else message = $t.face_180_live_video[$flow.stage]

    if (!+confirmation) return

    if (
      [STAGE.CAPTURING_FRONT, STAGE.CAPTURING_LEFT, STAGE.CAPTURING_RIGHT].includes($flow.stage) ||
      $flow.stage.startsWith("face_")
    )
      message = $t.face_180_live_video.face_placement_fixed
    else if ($flow.stage.startsWith("lighting_")) message = $t.face_180_live_video.lighting_fixed
  })

  $effect(() => {
    if ($flow.stage) untrack(() => $flow.stage && onChange($flow.stage))
  })

  let illumination = $derived(detections?.illumination)

  /**
   * Stages are named after capturing face parts, but variables represent the direction of the angle in which the head turns
   * User turns head to the right - the left side of the face is captured
   */
  // Right
  $effect(() => {
    if (
      $flow.stage &&
      [STAGE.ROTATING_LEFT, STAGE.CAPTURING_LEFT].includes($flow.stage) &&
      !+confirmation
    )
      right = clamp(detections?.face?.rotation?.yaw ?? 0, { max: 0 }) / -SIDE_CAPTURE_FACE_ANGLE
  })

  // Left
  $effect(() => {
    if (
      $flow.stage &&
      [STAGE.ROTATING_RIGHT, STAGE.CAPTURING_RIGHT].includes($flow.stage) &&
      !+confirmation
    )
      left = clamp(detections?.face?.rotation?.yaw ?? 0, { min: 0 }) / SIDE_CAPTURE_FACE_ANGLE
  })

  let isAngleDeviationError = $derived(
    includes([STAGE.FACE_PITCH_ANGLE_DEVIATION, STAGE.FACE_YAW_ANGLE_DEVIATION], $flow.stage),
  )
  let guideAxis = $state<AXIS>()

  $effect(() => {
    if (!+confirmation)
      guideAxis = stage === STAGE.FACE_PITCH_ANGLE_DEVIATION ? AXIS.PITCH : AXIS.YAW
  })
  let guideRotation = $derived(
    getGuideRotation({ axis: guideAxis, rotations: detections?.face?.rotation }),
  )

  const completeColorClass = "text-[#B5FF6A]"
  const guideClass = "!absolute left-1/2 -translate-x-1/2"
  const indicatorClass = "!absolute top-1/2 -translate-y-1/2"
</script>

<Layout {...props}>
  {#snippet faceBox()}
    {#if includes([STAGE.FACE_DISPLACED, STAGE.FACE_DISTANCE_CLOSE, STAGE.FACE_DISTANCE_FAR], $flow.stage)}
      <CameraFrame transition={fade} closure={+confirmation} success={+confirmation === 1} />
    {:else if detections?.face && !includes([STAGE.LIGHTING_INSUFFICIENT, STAGE.LIGHTING_EXCESSIVE, STAGE.LIGHTING_ANGLE_DEVIATION], $flow.stage)}
      {@const isComplete = $flow.stage === STAGE.CAPTURING_DONE}

      <CircleScanFrame transition={fade} class={isComplete ? completeColorClass : ""}>
        {#if isAngleDeviationError && guideAxis}
          <CircleScanFrame.Guide
            showTarget
            axis={guideAxis}
            rotation={guideRotation}
            direction={+confirmation
              ? null
              : getGuideDirection({
                  axis: guideAxis,
                  rotations: detections.face.rotation,
                  targetBounds:
                    guideAxis === AXIS.YAW
                      ? YAW_ANGLE_THRESHOLDS.perfect
                      : PITCH_ANGLE_THRESHOLDS.perfect,
                })}
            class={`angle-deviation-guide ${guideClass}`}
          />
        {:else if guideAxis && includes([STAGE.CAPTURING_LEFT, STAGE.CAPTURING_RIGHT, STAGE.ROTATING_LEFT, STAGE.ROTATING_RIGHT], $flow.stage)}
          <CircleScanFrame.Guide
            axis={guideAxis}
            rotation={guideRotation}
            direction={(includes([STAGE.ROTATING_RIGHT, STAGE.ROTATING_LEFT], $flow.stage) &&
              ($flow.stage === STAGE.ROTATING_RIGHT ? DIRECTION.LEFT : DIRECTION.RIGHT)) ||
              null}
            class={`rotation-guide ${guideClass} ${completeColorClass} ${
              isComplete ? "opacity-0 transition-opacity duration-300" : ""
            }`}
          />
          <CircleScanFrame.Indicator
            fill={left}
            class={["left-1/2 -translate-x-full -scale-x-100", indicatorClass, completeColorClass]}
            fillClass="rotation-progress-indicator"
          />
          <CircleScanFrame.Indicator
            fill={right}
            class={["right-1/2 translate-x-full", indicatorClass, completeColorClass]}
            fillClass="rotation-progress-indicator"
          />
        {/if}
      </CircleScanFrame>
    {/if}
  {/snippet}

  {#snippet frontLayer()}
    {#if isAngleDeviationError || (+confirmation && $flow.stage && [STAGE.CAPTURING_LEFT, STAGE.CAPTURING_RIGHT, STAGE.CAPTURING_FRONT].includes($flow.stage))}
      <StatusBacklight variant={isAngleDeviationError ? VARIANT.ERROR : VARIANT.SUCCESS} />
    {/if}
  {/snippet}

  {#snippet guidance()}
    <Reel key={message} backward={$flow.backward}>
      {#if message}
        <p class="whitespace-pre-wrap text-[1.75rem]/6">{message}</p>
      {/if}
    </Reel>
  {/snippet}

  {#snippet indicator()}
    {#if +confirmation}
      <Icon type={ICON.CHECKMARK_CIRCLE} transition={fade} size={44} />
    {:else if ($flow.stage === STAGE.LIGHTING_INSUFFICIENT || $flow.stage === STAGE.LIGHTING_EXCESSIVE) && illumination}
      <IlluminationIntensity
        value={illumination.intensity}
        thresholds={LIGHT_INTENSITY_THRESHOLDS}
      />
    {:else if $flow.stage === STAGE.LIGHTING_ANGLE_DEVIATION && illumination}
      <IlluminationDirection
        {facing}
        azimuth={illumination.azimuth}
        thresholds={LIGHT_AZIMUTH_THRESHOLDS}
      />
    {/if}
  {/snippet}
</Layout>
